import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { Form, Formik, FormikHelpers, useFormikContext } from "formik" // eslint-disable-line
import * as Yup from "yup"

import { useTranslation } from "react-i18next"

import { bonusEligibilityOptions, enumTenant } from "constants/bonus"
import { enumPromoTypes } from "constants/promo-codes"

import { tagsService } from "services/tags/tags.service"

import { BonusEligibility } from "types/api"

import { DropdownMenu } from "../../inputs/DropdownMenu"
import { InputField } from "../../inputs/InputField"
import { Modal } from "../Modal"

import { ActivityRequirements } from "./ActivityRequirements"
import { BonusOfferSelection } from "./BonusOfferSelection"
import { CacheBalance } from "./CacheBalance"
import { EligibilityCheckboxes } from "./EligibilityCheckboxes"
import { MainFields } from "./MainFields"
import { NoDepositBonus } from "./NoDepositBonus"

export interface FormValuesType {
  id: string
  code: string
  type: string
  active: boolean
  allowed_uses: number
  tenant_id: string
  bonus_id?: string
  bonus_amount?: number
  usd_amount?: number
  wager_requirements?: number
  days_to_expire?: number
  kyc: boolean
  email_verified_required?: boolean
  phone_verified_required?: boolean
  new_customers_only?: boolean
  required_tag_id: string
  min_deposit_amount_usd: number | null
  min_wager_amount_usd: number | null
  activity_period_days: number | null
  eligibility?: BonusEligibility | null
}

type TagOption = { name: string; value: string }
interface IProps {
  data?: any
  open: boolean
  handler: any
  onSubmit: (values: FormValuesType) => void
  promoBonuses: any
  promoBonusesSpins: any
  promoBonusesFreebets: any
  handleBonusModalCreate: (promoType: string) => void
  savedPromo?: { id: string; title: string } | null
}

export const validationSchema = Yup.object().shape({
  code: Yup.string().required(),
  type: Yup.string().required("This field is required"),
  tenant_id: Yup.string().required("This field is required"),
  allowed_uses: Yup.number().integer().min(0, "The value must not be a negative number"),
  bonus_id: Yup.string().when("type", {
    is: (val: string) => val === enumPromoTypes.DEPOSIT_BONUS || val === enumPromoTypes.FREESPINS || val === enumPromoTypes.FREEBET,
    then: Yup.string().required("Please choose one")
  }),
  usd_amount: Yup.number().when("type", {
    is: enumPromoTypes.CASH_BALANCE,
    then: Yup.number().required().min(1)
  }),
  bonus_amount: Yup.number().when("type", {
    is: enumPromoTypes.NO_DEPOSIT_BONUS,
    then: Yup.number().required().min(1)
  }),
  wager_requirements: Yup.number().when("type", {
    is: enumPromoTypes.NO_DEPOSIT_BONUS,
    then: Yup.number().required().min(1)
  }),
  days_to_expire: Yup.number().when("type", {
    is: enumPromoTypes.NO_DEPOSIT_BONUS,
    then: Yup.number().required().min(1)
  })
})

const defaultInitialValues: FormValuesType = {
  id: "",
  code: "",
  type: enumPromoTypes.CASH_BALANCE,
  active: false,
  allowed_uses: 0,
  tenant_id: enumTenant.Slotwhales,
  kyc: false,
  email_verified_required: false,
  phone_verified_required: false,
  new_customers_only: false,
  required_tag_id: "",
  min_deposit_amount_usd: null,
  min_wager_amount_usd: null,
  activity_period_days: null,
  eligibility: null
}

export const ModalPromoCode: FC<IProps> = ({
  data,
  open,
  handler,
  onSubmit,
  promoBonuses,
  promoBonusesSpins,
  promoBonusesFreebets,
  handleBonusModalCreate,
  savedPromo
}) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [availableTags, setAvailableTags] = useState<TagOption[]>([])
  const [tagsLoading, setTagsLoading] = useState(false)

  const loadTags = async () => {
    setTagsLoading(true)
    try {
      const result = await tagsService.getTags({ pageSize: 200 })
      if (result?.data) {
        setAvailableTags(result.data.map((t) => ({ name: t.name, value: t.id })))
      }
    } finally {
      setTagsLoading(false)
    }
  }

  useEffect(() => {
    if (open) loadTags()
  }, [open])

  const initialValues = useMemo(() => {
    let initValues: FormValuesType = defaultInitialValues
    if (data) {
      initValues = {
        ...data,
        bonus_id:
          data.type === enumPromoTypes.DEPOSIT_BONUS || data.type === enumPromoTypes.FREESPINS || data.type === enumPromoTypes.FREEBET
            ? data.bonus_id ?? ""
            : undefined,
        usd_amount: data.type === enumPromoTypes.CASH_BALANCE ? data.usd_amount ?? 0 : undefined,
        bonus_amount: data.type === enumPromoTypes.NO_DEPOSIT_BONUS ? data.bonus_amount ?? 0 : undefined,
        wager_requirements: data.type === enumPromoTypes.NO_DEPOSIT_BONUS ? data.wager_requirements ?? 0 : undefined,
        days_to_expire: data.type === enumPromoTypes.NO_DEPOSIT_BONUS ? data.days_to_expire ?? 0 : undefined,
        email_verified_required: data.type === enumPromoTypes.NO_DEPOSIT_BONUS ? data.email_verified_required ?? false : false,
        phone_verified_required: data.type === enumPromoTypes.NO_DEPOSIT_BONUS ? data.phone_verified_required ?? false : false,
        required_tag_id: data.required_tag_id ?? data.required_tag?.id ?? "",
        min_deposit_amount_usd: data.min_deposit_amount_usd ?? null,
        min_wager_amount_usd: data.min_wager_amount_usd ?? null,
        activity_period_days: data.activity_period_days ?? null,
        eligibility: data.eligibility ?? null
      }
    }
    return initValues
  }, [data])

  const onSubmitHandler = useCallback((values: FormValuesType, formikHelpers: FormikHelpers<any>) => {
    formikHelpers.setTouched({})
    // Empty eligibility means "inherit from bonus" — send null, not an empty string.
    onSubmit({ ...values, eligibility: values.eligibility || null })
    handler()
  }, [])

  return (
    <Modal open={open} handler={handler}>
      <div className="relative flex flex-col justify-between modal-box">
        <Formik<FormValuesType> initialValues={initialValues} onSubmit={onSubmitHandler} validationSchema={validationSchema}>
          {({ handleChange, values, touched, errors, handleSubmit }) => (
            <Form onSubmit={handleSubmit}>
              <SyncSavedPromo savedPromo={savedPromo} />
              <div className="flex flex-row space-x-4 mt-3">
                <div className="w-full">
                  <InputField
                    id="code"
                    name="code"
                    title={t("promo.promoCodeField")}
                    value={values.code}
                    onChange={handleChange}
                    invalid={!!(touched.code && errors.code)}
                    altLabelTwo={touched.code ? errors.code : ""}
                  />
                </div>
                <div className="w-full">
                  <InputField
                    id="allowed_uses"
                    name="allowed_uses"
                    title={t("promo.allowedUses")}
                    type="number"
                    value={values.allowed_uses}
                    onChange={handleChange}
                    invalid={!!(touched.allowed_uses && errors.allowed_uses)}
                    altLabelTwo={touched.allowed_uses ? errors.allowed_uses : ""}
                  />
                </div>
              </div>
              <div className="flex flex-row space-x-4 mt-3">
                <div className="w-full">
                  <MainFields />
                  {values.type === enumPromoTypes.CASH_BALANCE && <CacheBalance />}
                  {(values.type === enumPromoTypes.DEPOSIT_BONUS || values.type === enumPromoTypes.FREESPINS) && (
                    <BonusOfferSelection
                      bonusId={values.bonus_id}
                      bonuses={values.type === enumPromoTypes.FREESPINS ? promoBonusesSpins : promoBonuses}
                      onChange={(id) => {
                        handleChange({
                          target: { name: "bonus_id", value: id }
                        })
                      }}
                      onCreate={() => handleBonusModalCreate(values.type)}
                      touched={!!touched.bonus_id}
                      error={errors.bonus_id as string}
                      savedPromo={savedPromo}
                    />
                  )}
                  {values.type === enumPromoTypes.FREEBET && (
                    <BonusOfferSelection
                      bonusId={values.bonus_id}
                      bonuses={promoBonusesFreebets}
                      onChange={(id) => {
                        handleChange({
                          target: { name: "bonus_id", value: id }
                        })
                      }}
                      onCreate={() => handleBonusModalCreate(values.type)}
                      touched={!!touched.bonus_id}
                      error={errors.bonus_id as string}
                      savedPromo={savedPromo}
                    />
                  )}
                  {values.type === enumPromoTypes.NO_DEPOSIT_BONUS && <NoDepositBonus />}

                  {values.type !== enumPromoTypes.CASH_BALANCE && (
                    <div className="mt-3">
                      <DropdownMenu
                        id="eligibility"
                        name="eligibility"
                        title="Product eligibility"
                        placeholder="Inherit from bonus (no restriction)"
                        options={bonusEligibilityOptions}
                        value={values.eligibility ?? ""}
                        onChange={handleChange}
                      />
                      <p className="text-xs text-yellow-400 mt-2">
                        Restricts where the bonus money can be played. Leave empty to inherit the selected bonus&apos;s
                        setting.
                      </p>
                    </div>
                  )}

                  <EligibilityCheckboxes />

                  <div className="mt-3">
                    <DropdownMenu
                      title="Required Tag"
                      placeholder="No tag required"
                      options={availableTags}
                      value={values.required_tag_id}
                      onChange={handleChange}
                      name="required_tag_id"
                      id="required_tag_id"
                      loading={tagsLoading}
                    />
                    {values.required_tag_id && (
                      <p className="text-xs text-yellow-400 mt-2">Only users with this tag will be eligible for this promo code.</p>
                    )}
                  </div>

                  <ActivityRequirements />
                </div>
              </div>
              <div className="modal-action">
                <button
                  className="btn btn-secondary bg-[#ce4e79] text-white border-transparent hover:border-transparent hover:bg-[#ce4e79c5]"
                  onClick={handler}
                  type="button"
                >
                  {t("common.cancel")}
                </button>
                <button
                  className="btn btn-primary bg-[rgba(169,78,206,1)] border-transparent hover:bg-[rgba(169,78,206,0.7)] hover:border-transparent"
                  type="submit"
                >
                  {t("common.save")}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  )
}

const SyncSavedPromo = ({ savedPromo }: { savedPromo?: { id: string } | null }) => {
  const { setFieldValue } = useFormikContext<FormValuesType>()

  useEffect(() => {
    if (savedPromo?.id) {
      setFieldValue("bonus_id", savedPromo.id)
    }
  }, [savedPromo?.id])

  return null
}
